import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import {
    clearAdminCredentials,
    getStoredAdminCredentials,
    setState,
    getLeadingAuctionBid,
    terminateAuction,
    fetchServerState,
    setPrices,
    setTickers,
    setMaxOrderPrice,
    fetchLeaderboard,
    sendUserWsMessage,
    fetchLeaderboardHistory,
    saveRound,
    clearLeaderboardHistory,
    reloadLeaderboardHistory,
} from '../../lib/adminApi';
import './AdminDashboard.css';

const STATE_LABELS: Record<number, string> = {
    0: 'STOP',
    1: 'TRADE',
    2: 'TRADE WITH AUCTION',
    3: 'AUCTION',
};

const STATE_CSS: Record<number, string> = {
    0: 'state-stop',
    1: 'state-trade',
    2: 'state-trade-auction',
    3: 'state-auction',
};

const POLL_INTERVAL_MS = 2000;

interface Feedback {
    type: 'success' | 'error';
    text: string;
}

const AdminDashboard = () => {
    const navigate = useNavigate();
    const [currentState, setCurrentState] = useState<number | null>(null);
    const [auctionUser, setAuctionUser] = useState<string>('');
    const [auctionBid, setAuctionBid] = useState<number>(0);
    const [secondUser, setSecondUser] = useState<string>('');
    const [secondBid, setSecondBid] = useState<number>(0);
    const [feedback, setFeedback] = useState<Feedback | null>(null);
    const [busy, setBusy] = useState(false);
    const [priceRows, setPriceRows] = useState<{ id: number; ticker: string; price: string }[]>([
        { id: 0, ticker: '', price: '' },
    ]);
    const priceRowIdRef = useRef(1);
    const [leaderboardRaw, setLeaderboardRaw] = useState<string>('');
    const [leaderboardLoading, setLeaderboardLoading] = useState(false);
    const [roundName, setRoundName] = useState<string>('');
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [loggingOut, setLoggingOut] = useState(false);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const [tickerRows, setTickerRows] = useState<{ id: number; ticker: string }[]>([
        { id: 0, ticker: '' },
    ]);
    const tickerRowIdRef = useRef(1);
    const [maxOrderPrice, setMaxOrderPriceInput] = useState<string>('1000');

    const [wsTargetUsername, setWsTargetUsername] = useState<string>('');
    const [wsMessage, setWsMessage] = useState<string>('');
    const [wsSending, setWsSending] = useState(false);

    useEffect(() => {
        if (!getStoredAdminCredentials()) {
            navigate('/admin');
            return;
        }
        fetchServerState().then((state) => {
            if (state !== null) setCurrentState(state);
        });
    }, [navigate]);

    const applyAuctionData = useCallback((data: any) => {
        setAuctionUser(data.firstUser || '(none)');
        setAuctionBid(data.firstBid ?? 0);
        setSecondUser(data.secondUser || '');
        setSecondBid(data.secondBid ?? 0);
    }, []);

    const fetchLeadingBidSilent = useCallback(async () => {
        try {
            const res = await getLeadingAuctionBid();
            if (res.ok) {
                applyAuctionData(res.data);
            }
        } catch {
            // Silently ignore polling errors
        }
    }, [applyAuctionData]);

    const auctionActive = currentState === 2 || currentState === 3;

    useEffect(() => {
        if (auctionActive) {
            fetchLeadingBidSilent();
            pollRef.current = setInterval(fetchLeadingBidSilent, POLL_INTERVAL_MS);
        } else {
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
        }
        return () => {
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
        };
    }, [auctionActive, fetchLeadingBidSilent]);

    const showFeedback = (type: 'success' | 'error', text: string) => {
        setFeedback({ type, text });
        setTimeout(() => setFeedback(null), 4000);
    };

    const handleStartTrading = useCallback(async () => {
        const target = currentState === 3 ? 2 : 1;
        setBusy(true);
        try {
            const res = await setState(target);
            if (res.ok) {
                setCurrentState(res.data.newState);
                showFeedback(
                    'success',
                    `State changed to ${STATE_LABELS[res.data.newState] ?? res.data.newState}`,
                );
            } else {
                showFeedback('error', 'Failed to start trading.');
            }
        } catch {
            showFeedback('error', 'Could not reach the server.');
        } finally {
            setBusy(false);
        }
    }, [currentState]);

    const handleStopTrading = useCallback(async () => {
        const target = currentState === 2 ? 3 : 0;
        setBusy(true);
        try {
            const res = await setState(target);
            if (res.ok) {
                setCurrentState(res.data.newState);
                showFeedback(
                    'success',
                    `State changed to ${STATE_LABELS[res.data.newState] ?? res.data.newState}`,
                );
            } else {
                showFeedback('error', 'Failed to stop trading.');
            }
        } catch {
            showFeedback('error', 'Could not reach the server.');
        } finally {
            setBusy(false);
        }
    }, [currentState]);

    const handleStartAuction = useCallback(async () => {
        const target = currentState === 1 ? 2 : 3;
        setBusy(true);
        setAuctionUser('');
        setAuctionBid(0);
        setSecondUser('');
        setSecondBid(0);
        try {
            const res = await setState(target);
            if (res.ok) {
                setCurrentState(res.data.newState);
                showFeedback(
                    'success',
                    `State changed to ${STATE_LABELS[res.data.newState] ?? res.data.newState}`,
                );
            } else {
                showFeedback('error', 'Failed to start auction.');
            }
        } catch {
            showFeedback('error', 'Could not reach the server.');
        } finally {
            setBusy(false);
        }
    }, [currentState]);

    const handleFetchLeadingBid = useCallback(async () => {
        setBusy(true);
        try {
            const res = await getLeadingAuctionBid();
            if (res.ok) {
                applyAuctionData(res.data);
                showFeedback('success', 'Leading bid refreshed.');
            } else {
                showFeedback('error', 'Failed to fetch leading bid.');
            }
        } catch {
            showFeedback('error', 'Could not reach the server.');
        } finally {
            setBusy(false);
        }
    }, [applyAuctionData]);

    const handleTerminateAuction = useCallback(async () => {
        if (
            !window.confirm(
                'Terminate the auction? This will execute the winning bid and settle PnL.',
            )
        ) {
            return;
        }
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
        const nextState = currentState === 2 ? 1 : 0;
        setBusy(true);
        try {
            const res = await terminateAuction();
            if (res.ok) {
                applyAuctionData(res.data);
                setCurrentState(nextState);
                showFeedback(
                    'success',
                    `Auction terminated. Winner: ${res.data.firstUser || '(none)'} at ${res.data.firstBid ?? 0}`,
                );
            } else if (res.status === 423) {
                showFeedback('error', 'No auction is currently active.');
            } else {
                showFeedback('error', 'Failed to terminate auction.');
            }
        } catch {
            showFeedback('error', 'Could not reach the server.');
        } finally {
            setBusy(false);
        }
    }, [applyAuctionData, currentState]);

    const handleSetPrices = useCallback(async () => {
        const prices: Record<string, number> = {};
        for (const row of priceRows) {
            const t = row.ticker.trim().toUpperCase();
            if (!t) {
                showFeedback('error', 'Ticker name cannot be empty.');
                return;
            }
            const p = Number(row.price);
            if (!Number.isFinite(p) || p < 0) {
                showFeedback('error', `Price for "${t}" must be a non-negative number.`);
                return;
            }
            prices[t] = p;
        }

        if (Object.keys(prices).length === 0) {
            showFeedback('error', 'No valid ticker prices found.');
            return;
        }

        setBusy(true);
        try {
            // Requirement: when setting prices, force the system into NO TRADE (STOP) first.
            // If an auction is active, stopping requires terminating the auction.
            let effectiveState = currentState;
            if (effectiveState === null) {
                effectiveState = await fetchServerState();
                if (effectiveState !== null) setCurrentState(effectiveState);
            }

            if (effectiveState === 2 || effectiveState === 3) {
                const ok = window.confirm(
                    'An auction is currently active. To set prices, the system must stop, which will terminate and settle the auction. Continue?',
                );
                if (!ok) {
                    showFeedback('error', 'Price update cancelled.');
                    return;
                }

                const term = await terminateAuction();
                if (!term.ok) {
                    showFeedback('error', 'Failed to terminate auction. Prices not updated.');
                    return;
                }

                applyAuctionData(term.data);
                const postTermState = effectiveState === 2 ? 1 : 0;
                if (postTermState !== 0) {
                    await setState(0);
                }
                setCurrentState(0);
            } else if (effectiveState !== 0) {
                const stop = await setState(0);
                if (!stop.ok) {
                    showFeedback('error', 'Failed to switch to NO TRADE. Prices not updated.');
                    return;
                }
                setCurrentState(stop.data.newState);
            }

            const res = await setPrices(prices);
            if (res.ok) {
                showFeedback('success', res.data?.message || 'Prices updated.');
            } else {
                showFeedback('error', res.data?.message || 'Failed to set prices.');
            }
        } catch {
            showFeedback('error', 'Could not reach the server.');
        } finally {
            setBusy(false);
        }
    }, [priceRows, currentState, applyAuctionData]);

    const handleFetchLeaderboard = useCallback(async () => {
        setLeaderboardLoading(true);
        try {
            const res = await fetchLeaderboard();
            if (res.ok) {
                setLeaderboardRaw(res.data || '');
                showFeedback('success', 'Leaderboard refreshed.');
            } else {
                setLeaderboardRaw(res.data || '');
                showFeedback('error', 'Failed to fetch leaderboard.');
            }
        } catch {
            showFeedback('error', 'Could not reach the server.');
        } finally {
            setLeaderboardLoading(false);
        }
    }, []);

    const handleDownloadLeaderboard = useCallback(() => {
        const raw = leaderboardRaw?.trim();
        if (!raw) {
            showFeedback('error', 'Refresh the round leaderboard first.');
            return;
        }
        try {
            const parsed = JSON.parse(raw);
            const json = JSON.stringify(parsed, null, 2);
            const blob = new Blob([json], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const now = new Date();
            const stamp =
                now.getFullYear() +
                '-' +
                String(now.getMonth() + 1).padStart(2, '0') +
                '-' +
                String(now.getDate()).padStart(2, '0') +
                '_' +
                String(now.getHours()).padStart(2, '0') +
                '-' +
                String(now.getMinutes()).padStart(2, '0');
            a.download = `leaderboard_${stamp}.json`;
            a.click();
            URL.revokeObjectURL(url);
            showFeedback('success', 'Leaderboard downloaded.');
        } catch {
            showFeedback('error', 'Leaderboard data is not valid JSON.');
        }
    }, [leaderboardRaw]);

    const handleSaveRound = useCallback(async () => {
        const name = roundName.trim();
        if (!name) {
            showFeedback('error', 'Enter a round name (e.g. "Round 1").');
            return;
        }
        setBusy(true);
        try {
            const res = await saveRound(name);
            if (res.ok) {
                showFeedback('success', `Round "${name}" saved to leaderboard history.`);
                setRoundName('');
            } else {
                showFeedback('error', 'Failed to save round.');
            }
        } catch {
            showFeedback('error', 'Could not reach the server.');
        } finally {
            setBusy(false);
        }
    }, [roundName]);

    const handleClearHistory = useCallback(async () => {
        if (!window.confirm('Clear all leaderboard history? This cannot be undone.')) return;
        setBusy(true);
        try {
            const res = await clearLeaderboardHistory();
            if (res.ok) {
                showFeedback('success', 'Leaderboard history cleared.');
            } else {
                showFeedback('error', 'Failed to clear history.');
            }
        } catch {
            showFeedback('error', 'Could not reach the server.');
        } finally {
            setBusy(false);
        }
    }, []);

    const handleDownloadCumulativeLeaderboard = useCallback(async () => {
        setBusy(true);
        try {
            const data = await fetchLeaderboardHistory();
            if (!data || !data.rounds || data.rounds.length === 0) {
                showFeedback('error', 'No cumulative leaderboard history to download.');
                return;
            }
            const json = JSON.stringify(data, null, 2);
            const blob = new Blob([json], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const now = new Date();
            const stamp =
                now.getFullYear() +
                '-' +
                String(now.getMonth() + 1).padStart(2, '0') +
                '-' +
                String(now.getDate()).padStart(2, '0') +
                '_' +
                String(now.getHours()).padStart(2, '0') +
                '-' +
                String(now.getMinutes()).padStart(2, '0');
            a.download = `cumulative_leaderboard_${stamp}.json`;
            a.click();
            URL.revokeObjectURL(url);
            showFeedback('success', 'Cumulative leaderboard downloaded.');
        } catch {
            showFeedback('error', 'Could not reach the server.');
        } finally {
            setBusy(false);
        }
    }, []);

    const handleReloadCumulativeLeaderboard = useCallback(async () => {
        if (
            !window.confirm(
                'Reload leaderboard history from disk (leaderboard_history.json)? This discards any in-memory changes.',
            )
        ) {
            return;
        }
        setBusy(true);
        try {
            const res = await reloadLeaderboardHistory();
            if (res.ok) {
                showFeedback('success', 'Cumulative leaderboard history reloaded from disk.');
            } else if (res.status === 401) {
                showFeedback('error', 'Admin authentication failed.');
            } else {
                showFeedback('error', 'Failed to reload leaderboard history.');
            }
        } catch {
            showFeedback('error', 'Could not reach the server.');
        } finally {
            setBusy(false);
        }
    }, []);

    const addPriceRow = () => {
        setPriceRows((rows) => [...rows, { id: priceRowIdRef.current++, ticker: '', price: '' }]);
    };

    const removePriceRow = (id: number) => {
        setPriceRows((rows) => rows.filter((r) => r.id !== id));
    };

    const updatePriceRow = (id: number, field: 'ticker' | 'price', value: string) => {
        setPriceRows((rows) => rows.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
    };

    const addTickerRow = () => {
        setTickerRows((rows) => [...rows, { id: tickerRowIdRef.current++, ticker: '' }]);
    };

    const removeTickerRow = (id: number) => {
        setTickerRows((rows) => rows.filter((r) => r.id !== id));
    };

    const updateTickerRow = (id: number, value: string) => {
        setTickerRows((rows) => rows.map((r) => (r.id === id ? { ...r, ticker: value } : r)));
    };

    const handleSetTickers = useCallback(async () => {
        const maxPriceNum = Number(maxOrderPrice);
        if (!Number.isFinite(maxPriceNum) || !Number.isInteger(maxPriceNum) || maxPriceNum < 0) {
            showFeedback('error', 'Max price must be a non-negative integer.');
            return;
        }

        const tickers: string[] = [];
        const seen = new Set<string>();
        for (const row of tickerRows) {
            const t = row.ticker.trim().toUpperCase();
            if (!t) {
                showFeedback('error', 'Ticker name cannot be empty.');
                return;
            }
            if (!/^[A-Z0-9]+$/.test(t)) {
                showFeedback('error', `Invalid ticker "${t}". Use letters/numbers only.`);
                return;
            }
            if (!seen.has(t)) {
                seen.add(t);
                tickers.push(t);
            }
        }

        tickers.sort((a, b) => a.localeCompare(b));

        if (tickers.length === 0) {
            showFeedback('error', 'Add at least one ticker.');
            return;
        }
        if (
            !window.confirm(
                `Set tickers: ${tickers.join(', ')}?\nMax price: ${maxPriceNum}\n\nThis is destructive — ALL existing tickers and order books will be replaced.`,
            )
        ) {
            return;
        }
        setBusy(true);
        try {
            const [maxRes, tickRes] = await Promise.allSettled([
                setMaxOrderPrice(maxPriceNum),
                setTickers(tickers),
            ]);

            const maxOk = maxRes.status === 'fulfilled' && maxRes.value.ok;
            const tickOk = tickRes.status === 'fulfilled' && tickRes.value.ok;

            const appliedMaxPrice =
                maxRes.status === 'fulfilled' &&
                typeof (maxRes.value.data as any)?.maxPrice === 'number'
                    ? (maxRes.value.data as any).maxPrice
                    : maxPriceNum;

            const appliedTickers =
                tickRes.status === 'fulfilled' &&
                Array.isArray((tickRes.value.data as any)?.tickers)
                    ? ((tickRes.value.data as any).tickers as string[])
                    : tickers;

            if (tickOk && maxOk) {
                showFeedback(
                    'success',
                    `Tickers set: ${appliedTickers.join(', ')}. Max price set: ${appliedMaxPrice}.`,
                );
            } else {
                const tickErrMsg =
                    tickRes.status === 'fulfilled'
                        ? (tickRes.value.data as any)?.message?.errorMessage
                        : 'Could not reach the server.';
                const maxErrMsg =
                    maxRes.status === 'fulfilled'
                        ? (maxRes.value.data as any)?.message?.errorMessage
                        : 'Could not reach the server.';

                if (!tickOk && !maxOk) {
                    showFeedback(
                        'error',
                        `Failed to set tickers (${tickErrMsg || 'unknown'}); failed to set max price (${maxErrMsg || 'unknown'}).`,
                    );
                } else if (!tickOk) {
                    showFeedback('error', tickErrMsg || 'Failed to set tickers.');
                } else {
                    showFeedback(
                        'error',
                        `Tickers set: ${appliedTickers.join(', ')}, but failed to set max price (${maxErrMsg || 'unknown'}).`,
                    );
                }
            }
        } catch {
            showFeedback('error', 'Could not reach the server.');
        } finally {
            setBusy(false);
        }
    }, [tickerRows, maxOrderPrice]);

    const leaderboardDisplay = (() => {
        const text = leaderboardRaw?.trim();
        if (!text) return '';
        try {
            const parsed = JSON.parse(text);
            return JSON.stringify(parsed, null, 2);
        } catch {
            return leaderboardRaw;
        }
    })();

    const handleLogoutClick = () => {
        if (currentState !== null && currentState !== 0) {
            setShowLogoutModal(true);
        } else {
            clearAdminCredentials();
            navigate('/admin');
        }
    };

    const doLogout = () => {
        clearAdminCredentials();
        navigate('/admin');
    };

    const handleLogoutKeepRunning = () => {
        setShowLogoutModal(false);
        doLogout();
    };

    const handleLogoutAndStop = async () => {
        setLoggingOut(true);
        try {
            if (currentState === 2 || currentState === 3) {
                await terminateAuction();
                const postTermState = currentState === 2 ? 1 : 0;
                if (postTermState !== 0) {
                    await setState(0);
                }
            } else {
                await setState(0);
            }
        } catch {
            // Best-effort; proceed with logout regardless
        }
        setShowLogoutModal(false);
        doLogout();
    };

    const handleSendWsMessage = useCallback(async () => {
        const target = wsTargetUsername.trim();
        const message = wsMessage.trim();

        if (!target) {
            showFeedback('error', 'Target username is required (use "*" for all users).');
            return;
        }
        if (!message) {
            showFeedback('error', 'Message cannot be empty.');
            return;
        }

        setWsSending(true);
        try {
            const res = await sendUserWsMessage(target, message);
            if (res.ok) {
                showFeedback('success', target === '*' ? 'Message broadcasted.' : 'Message sent.');
                setWsMessage('');
            } else if (res.status === 401) {
                showFeedback('error', 'Admin authentication failed.');
            } else {
                showFeedback('error', 'Failed to send message.');
            }
        } catch {
            showFeedback('error', 'Could not reach the server.');
        } finally {
            setWsSending(false);
        }
    }, [wsTargetUsername, wsMessage]);

    const tradingOn = currentState === 1 || currentState === 2;
    const auctionOn = currentState === 2 || currentState === 3;
    const stateLabel =
        currentState !== null
            ? (STATE_LABELS[currentState] ?? `UNKNOWN (${currentState})`)
            : 'NOT FETCHED';
    const stateCss =
        currentState !== null ? (STATE_CSS[currentState] ?? 'state-unknown') : 'state-unknown';

    return (
        <div className="admin-dashboard">
            <div className="admin-header">
                <h1>Admin Dashboard</h1>
                <div className="admin-header-actions">
                    <span className={`state-indicator ${stateCss}`}>{stateLabel}</span>
                    <button className="btn btn-secondary" onClick={handleLogoutClick}>
                        Logout
                    </button>
                </div>
            </div>

            {feedback && (
                <div
                    className={`feedback-msg ${feedback.type === 'success' ? 'feedback-success' : 'feedback-error'}`}
                >
                    {feedback.text}
                </div>
            )}

            <div className="admin-panels">
                <div className="admin-panel">
                    <h2>Trading Controls</h2>
                    <p className="panel-desc">Start or stop trading for all participants.</p>
                    <div className="panel-buttons">
                        <button
                            className="btn btn-success"
                            disabled={busy || tradingOn}
                            onClick={handleStartTrading}
                        >
                            Start Trading
                        </button>
                        <button
                            className="btn btn-danger"
                            disabled={busy || !tradingOn}
                            onClick={handleStopTrading}
                        >
                            Stop Trading
                        </button>
                    </div>
                    {auctionOn && tradingOn && (
                        <p className="panel-note">
                            Stopping trading will switch to auction-only mode.
                        </p>
                    )}
                </div>

                <div className="admin-panel">
                    <h2>Auction Controls</h2>
                    <p className="panel-desc">Monitor and manage the active auction.</p>

                    <div className="auction-info">
                        <div className="auction-info-row">
                            <span className="auction-info-label">1st Place</span>
                            <span className="auction-info-value">
                                {auctionUser || '--'}
                                {auctionBid ? ` — $${auctionBid}` : ''}
                            </span>
                        </div>
                        <div className="auction-info-row">
                            <span className="auction-info-label">2nd Place</span>
                            <span className="auction-info-value">
                                {secondUser || '--'}
                                {secondBid ? ` — $${secondBid}` : ''}
                            </span>
                        </div>
                        <div className="auction-info-row auction-info-highlight">
                            <span className="auction-info-label">Winner Pays</span>
                            <span className="auction-info-value">
                                {auctionUser && auctionUser !== '(none)'
                                    ? secondBid
                                        ? `$${secondBid}`
                                        : '$0'
                                    : '--'}
                            </span>
                        </div>
                        {auctionOn && <div className="auction-live-badge">LIVE</div>}
                    </div>

                    <div className="panel-buttons">
                        <button
                            className="btn btn-warning"
                            disabled={busy || auctionOn}
                            onClick={handleStartAuction}
                        >
                            Start Auction
                        </button>
                        <button
                            className="btn btn-primary"
                            disabled={busy}
                            onClick={handleFetchLeadingBid}
                        >
                            Refresh Leading Bid
                        </button>
                        <button
                            className="btn btn-danger"
                            disabled={busy || !auctionOn}
                            onClick={handleTerminateAuction}
                        >
                            Terminate Auction
                        </button>
                    </div>
                    {auctionOn && tradingOn && (
                        <p className="panel-note">
                            Stopping trading will keep the auction running.
                        </p>
                    )}
                </div>

                <div className="admin-panel">
                    <h2>Set Prices</h2>
                    <p className="panel-desc">Update market prices for existing tickers.</p>

                    <div className="ticker-rows">
                        {priceRows.map((row) => (
                            <div key={row.id} className="ticker-row">
                                <input
                                    className="ticker-input"
                                    type="text"
                                    placeholder="TICKER"
                                    value={row.ticker}
                                    onChange={(e) =>
                                        updatePriceRow(row.id, 'ticker', e.target.value)
                                    }
                                    spellCheck={false}
                                    disabled={busy}
                                />
                                <input
                                    className="price-input"
                                    type="number"
                                    placeholder="Price"
                                    value={row.price}
                                    onChange={(e) =>
                                        updatePriceRow(row.id, 'price', e.target.value)
                                    }
                                    min={0}
                                    disabled={busy}
                                />
                                <button
                                    className="btn btn-danger ticker-remove-btn"
                                    onClick={() => removePriceRow(row.id)}
                                    disabled={busy}
                                    title="Remove"
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>

                    <div className="panel-buttons ticker-actions">
                        <button className="btn btn-secondary" disabled={busy} onClick={addPriceRow}>
                            + Add Ticker
                        </button>
                        <button
                            className="btn btn-primary"
                            disabled={busy || priceRows.length === 0}
                            onClick={handleSetPrices}
                        >
                            Set Prices
                        </button>
                        <button
                            className="btn btn-secondary"
                            disabled={busy}
                            onClick={() => {
                                priceRowIdRef.current = 1;
                                setPriceRows([{ id: 0, ticker: '', price: '' }]);
                            }}
                        >
                            Reset
                        </button>
                    </div>

                    <p className="panel-note">Tip: values must be non-negative numbers.</p>
                </div>

                <div className="admin-panel">
                    <h2>Set Tickers</h2>
                    <p className="panel-desc">
                        Replace the entire ticker universe. All existing order books are cleared.
                    </p>

                    <div className="ticker-rows">
                        {tickerRows.map((row) => (
                            <div key={row.id} className="ticker-row">
                                <input
                                    className="ticker-input"
                                    type="text"
                                    placeholder="TICKER"
                                    value={row.ticker}
                                    onChange={(e) => updateTickerRow(row.id, e.target.value)}
                                    spellCheck={false}
                                    disabled={busy}
                                />
                                <button
                                    className="btn btn-danger ticker-remove-btn"
                                    onClick={() => removeTickerRow(row.id)}
                                    disabled={busy}
                                    title="Remove"
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>

                    <div className="admin-field-row">
                        <span className="admin-field-label">Max Price</span>
                        <input
                            className="price-input"
                            type="number"
                            placeholder="1000"
                            value={maxOrderPrice}
                            onChange={(e) => setMaxOrderPriceInput(e.target.value)}
                            disabled={busy}
                            min={0}
                            step={1}
                        />
                    </div>

                    <div className="panel-buttons ticker-actions">
                        <button
                            className="btn btn-secondary"
                            disabled={busy}
                            onClick={addTickerRow}
                        >
                            + Add Ticker
                        </button>
                        <button
                            className="btn btn-danger"
                            disabled={busy || tickerRows.length === 0}
                            onClick={handleSetTickers}
                        >
                            Set Tickers
                        </button>
                        <button
                            className="btn btn-secondary"
                            disabled={busy}
                            onClick={() => {
                                tickerRowIdRef.current = 1;
                                setTickerRows([{ id: 0, ticker: '' }]);
                            }}
                        >
                            Reset
                        </button>
                    </div>

                    <p className="panel-note">
                        Warning: destructive — replaces all tickers and clears all order books.
                    </p>
                </div>

                <div className="admin-panel">
                    <h2>Round Leaderboard</h2>
                    <p className="panel-desc">
                        Current round only. Fetch from the server to see live PnL.
                    </p>

                    <div className="panel-buttons">
                        <button
                            className="btn btn-primary"
                            disabled={leaderboardLoading}
                            onClick={handleFetchLeaderboard}
                        >
                            {leaderboardLoading ? 'Refreshing...' : 'Refresh Round Leaderboard'}
                        </button>
                        <button
                            className="btn btn-secondary"
                            disabled={leaderboardLoading}
                            onClick={handleDownloadLeaderboard}
                        >
                            Download JSON
                        </button>
                        <button
                            className="btn btn-secondary"
                            disabled={leaderboardLoading}
                            onClick={() => setLeaderboardRaw('')}
                        >
                            Clear
                        </button>
                    </div>

                    <pre className="admin-pre">
                        {leaderboardDisplay || 'No round leaderboard fetched yet.'}
                    </pre>
                </div>

                <div className="admin-panel">
                    <h2>Send WS Message</h2>
                    <p className="panel-desc">
                        Send a message to a specific user (by username) or to all connected users
                        (use "*"). Messages appear in the user dashboard MessageViewer.
                    </p>

                    <div className="admin-field-row">
                        <span className="admin-field-label">Username</span>
                        <input
                            className="admin-input"
                            type="text"
                            placeholder='e.g. trader1 or "*"'
                            value={wsTargetUsername}
                            onChange={(e) => setWsTargetUsername(e.target.value)}
                            spellCheck={false}
                            disabled={busy || wsSending}
                        />
                    </div>

                    <textarea
                        className="admin-textarea"
                        rows={4}
                        placeholder="Message text"
                        value={wsMessage}
                        onChange={(e) => setWsMessage(e.target.value)}
                        disabled={busy || wsSending}
                    />

                    <div className="panel-buttons">
                        <button
                            className="btn btn-primary"
                            disabled={busy || wsSending}
                            onClick={handleSendWsMessage}
                        >
                            {wsSending ? 'Sending...' : 'Send'}
                        </button>
                        <button
                            className="btn btn-secondary"
                            disabled={busy || wsSending}
                            onClick={() => setWsMessage('')}
                        >
                            Clear
                        </button>
                    </div>
                    <p className="panel-note">Tip: use username "*" to broadcast.</p>
                </div>
                <div className="admin-panel">
                    <h2>Save Round</h2>
                    <p className="panel-desc">
                        Snapshot the current leaderboard as a round result for the public
                        leaderboard.
                    </p>

                    <div className="save-round-row">
                        <input
                            className="ticker-input save-round-input"
                            type="text"
                            placeholder="Round name (e.g. Round 1)"
                            value={roundName}
                            onChange={(e) => setRoundName(e.target.value)}
                            disabled={busy}
                            spellCheck={false}
                        />
                        <button
                            className="btn btn-success"
                            disabled={busy || !roundName.trim()}
                            onClick={handleSaveRound}
                        >
                            Save Round
                        </button>
                    </div>

                    <div className="panel-buttons" style={{ marginTop: 10 }}>
                        <Link to="/leaderboard" className="btn btn-primary lb-view-link">
                            View Public Leaderboard
                        </Link>
                        <button
                            className="btn btn-secondary"
                            disabled={busy}
                            onClick={handleDownloadCumulativeLeaderboard}
                            title="Download rounds and cumulative leaderboard as JSON"
                        >
                            Download cumulative JSON
                        </button>
                        <button
                            className="btn btn-secondary"
                            disabled={busy}
                            onClick={handleReloadCumulativeLeaderboard}
                            title="Reload leaderboard_history.json from server disk"
                        >
                            Reload from disk
                        </button>
                        <button
                            className="btn btn-danger"
                            disabled={busy}
                            onClick={handleClearHistory}
                        >
                            Clear History
                        </button>
                    </div>
                </div>
            </div>

            {showLogoutModal && (
                <div className="modal-overlay">
                    <div className="modal-box">
                        <h3>System Still Active</h3>
                        <p>
                            The system is currently in{' '}
                            <strong>{STATE_LABELS[currentState!] ?? 'ACTIVE'}</strong> mode. What
                            would you like to do?
                        </p>
                        <div className="modal-buttons">
                            <button
                                className="btn btn-danger"
                                disabled={loggingOut}
                                onClick={handleLogoutAndStop}
                            >
                                {loggingOut ? 'Stopping...' : 'Stop System & Logout'}
                            </button>
                            <button
                                className="btn btn-warning"
                                disabled={loggingOut}
                                onClick={handleLogoutKeepRunning}
                            >
                                Logout Without Stopping
                            </button>
                            <button
                                className="btn btn-secondary"
                                disabled={loggingOut}
                                onClick={() => setShowLogoutModal(false)}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminDashboard;
